import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { QueuesListPane } from '@/components/settings/queues/queues-list-pane';
import { QueueEditorPane } from '@/components/settings/queues/queue-editor-pane';
import {
  createQueueDefinition,
  deleteQueueDefinition,
  fetchQueueDefinitions,
  updateQueueDefinition,
} from '@/lib/api/queues';
import { fetchTenants } from '@/lib/api/tenants';
import {
  duplicateQueueDraft,
  makeQueue,
  normalizeQueueDraft,
  sortInClinic,
  tenantsToClinicOptions,
} from '@/lib/queues';

// Patient Queue config: the list ↔ single-queue editor flow. The list comes from the server
// (queues are only consumed here, so there's no global store); the editor saves one queue at a
// time with an explicit PUT and patches the list on success.
export function QueuesSubPage({ canEdit = true }) {
  const [queues, setQueues] = useState([]);
  const [clinicOptions, setClinicOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState('');
  const [reordering, setReordering] = useState(false);
  const [view, setView] = useState({ mode: 'list' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchQueueDefinitions()
      .then((items) => {
        if (cancelled) return;
        setQueues(items);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoading(false);
        toast.error(error instanceof Error ? error.message : 'Could not load queues');
      });
    // Clinics are tenants — same source as the calendar/slots filters. A failure here only
    // affects the attachment dropdown (labels fall back to the raw id); it never blocks queues.
    fetchTenants()
      .then((res) => {
        if (cancelled) return;
        setClinicOptions(tenantsToClinicOptions(res?.items));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('queue editor: failed to load clinics (tenants)', error);
        toast.error(
          error instanceof Error ? `Could not load clinics: ${error.message}` : 'Could not load clinics',
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await createQueueDefinition({ body: makeQueue() });
      setQueues((prev) => [...prev, created]);
      setView({ mode: 'edit', queue: created });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create queue');
    } finally {
      setCreating(false);
    }
  };

  // Duplicate = POST a copy of the whole definition (the list holds full documents, so nothing
  // extra has to be fetched) and open it, since renaming/tweaking is what follows a duplicate.
  const handleDuplicate = async (queue) => {
    if (duplicatingId) return;
    setDuplicatingId(queue.id);
    try {
      const created = await createQueueDefinition({ body: duplicateQueueDraft(queue, queues) });
      setQueues((prev) => [...prev, created]);
      toast.success(`Duplicated as “${created.name}”.`);
      setView({ mode: 'edit', queue: created });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not duplicate queue');
    } finally {
      setDuplicatingId('');
    }
  };

  const handleEdit = (id) => {
    const queue = queues.find((q) => q.id === id);
    if (queue) setView({ mode: 'edit', queue });
  };

  const handleDelete = async (queue) => {
    const ok = window.confirm(`Delete “${queue.name || 'this queue'}”? This can't be undone here.`);
    if (!ok) return;
    try {
      await deleteQueueDefinition({ queueId: queue.id });
      setQueues((prev) => prev.filter((q) => q.id !== queue.id));
      toast.success('Queue deleted.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete queue');
    }
  };

  const handleSaved = (updated) => {
    setQueues((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
  };

  // Swap a queue with its neighbour inside its clinic group and persist immediately. The list
  // holds FULL definitions, so a whole-document PUT via normalizeQueueDraft is safe (and the
  // normalizer passes callStaffReasons through, so reordering never wipes them). A legacy tie
  // (equal displayOrder, e.g. several 0s) is resolved by renumbering the whole group 0..n-1 in
  // the new visual order — the first click normalizes, later clicks PUT exactly two queues.
  const handleReorder = async (queue, direction) => {
    if (reordering) return;
    const group = queues.filter((q) => (q.clinicId || '') === (queue.clinicId || '')).sort(sortInClinic);
    const i = group.findIndex((q) => q.id === queue.id);
    const j = direction === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= group.length) return;
    const a = group[i];
    const b = group[j];
    let updates;
    if ((a.displayOrder ?? 0) !== (b.displayOrder ?? 0)) {
      updates = [
        { ...a, displayOrder: b.displayOrder ?? 0 },
        { ...b, displayOrder: a.displayOrder ?? 0 },
      ];
    } else {
      const next = [...group];
      [next[i], next[j]] = [next[j], next[i]];
      updates = next
        .map((q, index) => ({ ...q, displayOrder: index }))
        .filter((q) => (group.find((g) => g.id === q.id)?.displayOrder ?? 0) !== q.displayOrder);
    }
    if (updates.length === 0) return;
    const previous = queues;
    setQueues((prev) => prev.map((q) => updates.find((u) => u.id === q.id) ?? q));
    setReordering(true);
    try {
      const saved = await Promise.all(
        updates.map((u) => updateQueueDefinition({ queueId: u.id, body: normalizeQueueDraft(u) })),
      );
      setQueues((prev) => prev.map((q) => saved.find((s) => s.id === q.id) ?? q));
    } catch (error) {
      // One of the PUTs may have landed — revert locally, then refetch to converge on the server.
      setQueues(previous);
      toast.error(error instanceof Error ? error.message : 'Could not reorder queues');
      fetchQueueDefinitions()
        .then(setQueues)
        .catch(() => {});
    } finally {
      setReordering(false);
    }
  };

  if (view.mode === 'edit') {
    return (
      <QueueEditorPane
        key={view.queue.id}
        initialQueue={view.queue}
        canEdit={canEdit}
        clinicOptions={clinicOptions}
        queues={queues}
        onBack={() => setView({ mode: 'list' })}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <QueuesListPane
      queues={queues}
      loading={loading}
      canEdit={canEdit}
      creating={creating}
      duplicatingId={duplicatingId}
      reordering={reordering}
      clinicOptions={clinicOptions}
      onCreate={handleCreate}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      onReorder={handleReorder}
    />
  );
}
